import React from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './BuyerProtectionPage.module.css';

const IMG = '/static/images/buyer-protection';

const layoutAreas = `
  topbar
  main
  footer
`;

// ---------- Schema ----------

function buildPageSchema(rootURL) {
  return {
    '@type': 'Service',
    name: 'Avento Buyer Protection',
    url: `${rootURL}/buyer-protection`,
    serviceType: 'Escrow and Buyer Protection',
    description:
      'Escrow payments, listing verification, authenticity checks, money-back guarantees, and easy returns for buyers of used skydiving gear on Avento.',
    provider: {
      '@type': 'Organization',
      '@id': `${rootURL}#organization`,
      name: 'Avento Gear GmbH',
      url: rootURL,
    },
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
    audience: {
      '@type': 'Audience',
      audienceType: 'Skydivers buying used gear',
    },
    termsOfService: `${rootURL}/terms-of-service`,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Buyer Protection Features',
      itemListElement: [
        {
          '@type': 'Offer',
          name: 'Payment via Escrow',
          description:
            'Funds held in a secure escrow account for 14 days after delivery so the buyer can have the gear inspected by their rigger before payout.',
        },
        {
          '@type': 'Offer',
          name: 'Listing Authenticity Checks',
          description:
            'Every listing is individually reviewed by the Avento team before publication to flag suspicious gear and counterfeit risk.',
        },
        {
          '@type': 'Offer',
          name: 'Money-back Guarantee',
          description:
            'Full refund within 14 days of delivery if the gear is misrepresented or not as described in the listing.',
        },
        {
          '@type': 'Offer',
          name: 'Easy Returns',
          description:
            'Simple 3-step return process: dispute in-app, ship the gear back, receive a full refund.',
        },
        {
          '@type': 'Offer',
          name: 'Vetted Sellers',
          description:
            'All sellers are vetted during onboarding and must adhere to Avento selling and shipping guidelines.',
        },
        {
          '@type': 'Offer',
          name: 'Dedicated Customer Support',
          description:
            'Avento support team handles disputes fairly and promptly, typically resolving within 30 days.',
        },
      ],
    },
  };
}

// ---------- Main component ----------

const BuyerProtectionPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();

  const rootURL = config.marketplaceRootURL || '';
  const schemaTitle = 'Avento Buyer Protection | Buy Skydiving Gear Safely';
  const schemaDescription =
    'Escrow payments, listing verification, returns. Through the buyer protection on Avento you can buy skydiving gear with full confidence.';
  const pageSchema = buildPageSchema(rootURL);

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      schema={pageSchema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="BuyerProtectionPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                {/* Page header */}
                <div className={css.pageHeader}>
                  <div className={css.pageHeaderInner}>
                    <h1 className={css.pageTitle}>Avento Buyer Protection</h1>
                    <p className={css.pageSubtitle}>Safe, Reliable, Easy</p>
                  </div>
                </div>

                {/* Protection grid 1: 2 columns x 3 rows alternating cards & images */}
                <section className={css.section}>
                  <div className={css.container}>
                    <div className={css.protectionGridWrapper}>
                      <div className={css.protectionGrid}>
                        {/* (1,1) Card: Payment via Escrow */}
                        <div className={css.protectionCard}>
                          <h2 className={css.protectionHeading}>
                            Payment via Escrow Service
                          </h2>
                          <p className={css.protectionText}>
                            Our Escrow Service is just like your Skydiving
                            helmet - it protects you when things go south. We
                            keep your money in our Escrow Account for 14 Days
                            after you received the gear. You have time to bring
                            the gear to your rigger for inspection. Only then do
                            we payout the money to the seller
                          </p>
                          <a
                            href="/how-to-buy"
                            className={css.protectionLink}
                          >
                            Learn more about the transaction process
                          </a>
                        </div>

                        {/* (1,2) Image: Skydivers jumping - hidden on mobile */}
                        <img
                          src={`${IMG}/exit-wide.webp`}
                          alt="Skydivers jumping from a plane"
                          className={`${css.protectionImage} ${css.protectionImageHiddenMobile}`}
                          width={800}
                          height={304}
                          loading="lazy"
                        />

                        {/* (2,1) Image: Skydivers celebrating */}
                        <img
                          src={`${IMG}/check.webp`}
                          alt="Skydivers celebrating after a jump"
                          className={css.protectionImage}
                          width={800}
                          height={451}
                          loading="lazy"
                        />

                        {/* (2,2) Card: Commitment to Authenticity */}
                        <div className={css.protectionCard}>
                          <h2 className={css.protectionHeading}>
                            Commitment to Authenticity
                          </h2>
                          <p className={css.protectionText}>
                            We're committed to authenticity. Our team works
                            hard to flag suspicious listings early and prevent
                            issues before they ever reach you. We scan every
                            listing individually before it is posted.
                          </p>
                        </div>

                        {/* (3,1) Card: Money-back guarantee */}
                        <div className={css.protectionCard}>
                          <h2 className={css.protectionHeading}>
                            Money-back guarantee for misrepresentation
                          </h2>
                          <p className={css.protectionText}>
                            If a listing does slip through and the gear isn't
                            as promised, whether it's broken or not in the
                            described condition, you're covered by our
                            money-back guarantee for 14 days after delivery to
                            initiate a return and receive a full refund quickly
                            and easily.
                          </p>
                        </div>

                        {/* (3,2) Image: Skydiver under canopy */}
                        <img
                          src={`${IMG}/canopy.webp`}
                          alt="Skydiver under canopy"
                          className={css.protectionImage}
                          width={800}
                          height={533}
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                </section>

                {/* Returns Made Easy */}
                <section className={css.section}>
                  <div className={css.container}>
                    <h2 className={css.returnHeading}>Returns made Easy</h2>
                    <div className={css.protectionReturnsGrid}>
                      <div className={css.protectionReturnCard}>
                        <img
                          src={`${IMG}/icon-support.png`}
                          alt=""
                          className={css.protectionReturnIcon}
                          width={40}
                          height={40}
                          loading="lazy"
                        />
                        <h3 className={css.protectionReturnHeading}>
                          1. Contact us
                        </h3>
                        <p className={css.protectionReturnText}>
                          Dispute the order on Avento within 14 days of
                          receiving your gear. We will confirm that your
                          objection has been received in time and initiate
                          further steps.
                        </p>
                      </div>
                      <div className={css.protectionReturnCard}>
                        <img
                          src={`${IMG}/icon-shipping.png`}
                          alt=""
                          className={css.protectionReturnIcon}
                          width={40}
                          height={40}
                          loading="lazy"
                        />
                        <h3 className={css.protectionReturnHeading}>
                          2. Ship the gear back
                        </h3>
                        <p className={css.protectionReturnText}>
                          After we confirmed the misrepresentation, you send the
                          gear back in it's original packaging. You will be
                          responsible for the return shipping cost in most
                          cases.
                        </p>
                      </div>
                      <div className={css.protectionReturnCard}>
                        <img
                          src={`${IMG}/icon-refund.png`}
                          alt=""
                          className={css.protectionReturnIcon}
                          width={40}
                          height={40}
                          loading="lazy"
                        />
                        <h3 className={css.protectionReturnHeading}>
                          3. Receive a refund of the full amount
                        </h3>
                        <p className={css.protectionReturnText}>
                          Finally we will quickly refund you the full purchase
                          price.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Protection grid 2: 2 columns x 2 rows */}
                <section className={`${css.section} ${css.lastSection}`}>
                  <div className={css.container}>
                    <div className={css.protectionGridWrapper}>
                      <div className={css.protectionGrid2}>
                        {/* (1,1) Image: Skydiver dropping */}
                        <img
                          src={`${IMG}/drop.webp`}
                          alt="Skydiver jumping out of plane"
                          className={`${css.protectionImage} ${css.imageDropMobileOrder}`}
                          width={800}
                          height={511}
                          loading="lazy"
                        />

                        {/* (1,2) Card: Seller Guidelines */}
                        <div className={`${css.protectionCard} ${css.cardSellerGuidelinesMobileOrder}`}>
                          <h2 className={css.protectionHeading}>
                            Seller Guidelines
                          </h2>
                          <p className={css.protectionText}>
                            We apply seller guidelines and vet every seller
                            during onboarding as well as possible, so you know
                            who you're dealing with. Furthermore, sellers have
                            to adhere to guidelines in selling and shipping
                            gear.
                          </p>
                        </div>

                        {/* (2,1) Card: Avento's Support */}
                        <div className={`${css.protectionCard} ${css.cardAventoSupportMobileOrder}`}>
                          <h2 className={css.protectionHeading}>
                            Avento's Support
                          </h2>
                          <p className={css.protectionText}>
                            Throughout the process, we're here for you. If
                            there's ever an issue, our support team is ready to
                            help quickly and fairly. You can count on us.
                          </p>
                        </div>

                        {/* (2,2) Image: Avento founders */}
                        <img
                          src={`${IMG}/team-support.webp`}
                          alt="Avento Co-Founders Lean and Tim"
                          className={`${css.protectionImage} ${css.protectionImageTeamSupport} ${css.imageTeamSupportMobileOrder}`}
                          width={800}
                          height={452}
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const BuyerProtectionPage = connect(mapStateToProps)(
  BuyerProtectionPageComponent
);

export default BuyerProtectionPage;
